/**
 * EntityList - Renders a list of entities with optional section header
 *
 * Supports carousel (horizontal scroll), grid, and rows layouts.
 * Uses EntityHeader for each entity card (row variant for the rows layout).
 */

import { type ReactNode, useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { EntityHeader } from "@/components/entity-display/sections/EntityHeader";
import { LoadMoreButton } from "./InfiniteScrollSentinel";
import { SectionHeader, type SectionHeaderProps } from "./SectionHeader";
import { sortEntityIds, type EntitySortComparator } from "@/utils/entitySort";
import { entitiesService } from "@/services";
import { useToast } from "@/hooks/use-toast";

type Layout = "carousel" | "grid" | "rows";

interface EntityListProps {
  /** Array of entity IDs to render */
  entityIds: string[];
  /** Optional entities for React Query cache hydration */
  entities?: unknown[];
  /** Optional sort comparator. Use a function or "name" for alphabetical. Requires `entities`. */
  sort?: EntitySortComparator;
  /** Optional section header config */
  header?: SectionHeaderProps;
  /** Layout mode - carousel (horizontal scroll), grid, or rows (compact list rows) */
  layout?: Layout;
  /** Card width class for carousel mode (default: w-72) */
  cardWidth?: string;
  /** Grid columns class for grid mode */
  gridColumns?: string;
  /** Additional className for the container */
  className?: string;
  /** Empty state content when no entities */
  emptyState?: ReactNode;
  /** How to fit images in cards - contain shows full image, cover fills and crops */
  imageFit?: "contain" | "cover";
  /** Enable hide functionality on entity cards */
  enableHide?: boolean;
  /** Optional callback when entity is hidden (for parent to refetch, etc) */
  onEntityHidden?: (entityId: string) => void;
  /** Optional hover callback for map pin highlighting. */
  onEntityHover?: (entityId: string | null) => void;
  /** Whether selection mode is active */
  isEditMode?: boolean;
  /** Set of currently selected entity IDs */
  selectedIds?: Set<string>;
  /** Called when an entity is toggled in selection mode */
  onToggleSelect?: (entityId: string) => void;
  /** Skip auth for API calls (public/anonymous access) */
  skipAuth?: boolean;
  /** Enable lazy rendering for carousels with many items */
  virtualize?: boolean;
}

const DEFAULT_CARD_WIDTH = "w-72";
const DEFAULT_GRID_COLUMNS = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
// One column on phones; as many ~20rem columns as fit beyond that.
const ROWS_COLUMNS = "grid grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),1fr))] gap-x-6";
const ROWS_PAGE_SIZE = 15;


export function EntityList({
  entityIds,
  entities,
  sort,
  header,
  layout = "carousel",
  cardWidth = DEFAULT_CARD_WIDTH,
  gridColumns = DEFAULT_GRID_COLUMNS,
  className,
  emptyState,
  imageFit,
  enableHide = false,
  onEntityHidden,
  onEntityHover,
  isEditMode = false,
  selectedIds,
  onToggleSelect,
  skipAuth = false,
  virtualize = false,
}: EntityListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());

  // Handle hiding an entity
  const handleHide = useCallback(
    async (entityId: string) => {
      try {
        await entitiesService.deleteEntity(entityId);
        queryClient.removeQueries({ queryKey: ["entity", entityId] });
        setHiddenIds(prev => {
          const next = new Set(prev);
          next.add(entityId);
          return next;
        });
        onEntityHidden?.(entityId);
      } catch (error) {
        console.error("Failed to hide entity:", error);
        toast({
          title: "Couldn't hide this",
          description: "Please try again in a moment.",
        });
      }
    },
    [onEntityHidden, toast, queryClient],
  );

  // Sort entityIds when a comparator and entities are provided
  const sortedIds = useMemo(() => sortEntityIds(entityIds, entities, sort), [entityIds, entities, sort]);

  // Filter out hidden entities
  const visibleIds = useMemo(
    () => (hiddenIds.size > 0 ? sortedIds.filter(id => !hiddenIds.has(id)) : sortedIds),
    [sortedIds, hiddenIds],
  );

  // Carousel windowing: render in batches as the user scrolls (opt-in via virtualize prop)
  // Hooks must be called before any early returns to satisfy React's rules of hooks.
  const CAROUSEL_PAGE_SIZE = 20;
  const isCarousel = layout === "carousel";
  const shouldVirtualize = virtualize && isCarousel && visibleIds.length > CAROUSEL_PAGE_SIZE;
  const [carouselLimit, setCarouselLimit] = useState(CAROUSEL_PAGE_SIZE);
  const carouselSentinelRef = useRef<HTMLDivElement>(null);

  // Reset limit when the underlying data changes (e.g. items added/removed)
  const visibleCount = visibleIds.length;
  useEffect(() => {
    setCarouselLimit(CAROUSEL_PAGE_SIZE);
  }, [visibleCount]);

  useEffect(() => {
    if (!shouldVirtualize) return;
    const el = carouselSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCarouselLimit(prev => Math.min(prev + CAROUSEL_PAGE_SIZE, visibleIds.length));
        }
      },
      { rootMargin: "0px 600px 0px 0px" },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [shouldVirtualize, visibleIds.length]);

  // Rows sit among other sections, so a long list grows on request rather than burying them.
  const [rowLimit, setRowLimit] = useState(ROWS_PAGE_SIZE);
  const isRows = layout === "rows";

  const renderedIds = isRows
    ? visibleIds.slice(0, rowLimit)
    : shouldVirtualize
      ? visibleIds.slice(0, carouselLimit)
      : visibleIds;
  const remainingRows = isRows ? visibleIds.length - renderedIds.length : 0;

  // Empty state
  if (visibleIds.length === 0 && emptyState) {
    return (
      <div className={className}>
        {header && <SectionHeader {...header} />}
        {emptyState}
      </div>
    );
  }

  if (visibleIds.length === 0) {
    return null;
  }

  const listClasses = isCarousel
    ? "flex gap-2.5 overflow-x-auto scrollbar-hide -mx-gutter px-gutter"
    : isRows
      ? ROWS_COLUMNS
      : cn("grid gap-4", gridColumns);

  return (
    <div className={className}>
      {header && <SectionHeader {...header} />}

      <div className={listClasses}>
        {renderedIds.map(entityId => {
          const isSelected = selectedIds?.has(entityId) ?? false;
          return (
            <div
              key={entityId}
              onMouseEnter={onEntityHover ? () => onEntityHover(entityId) : undefined}
              onMouseLeave={onEntityHover ? () => onEntityHover(null) : undefined}
              onFocus={onEntityHover ? () => onEntityHover(entityId) : undefined}
              onBlur={onEntityHover ? () => onEntityHover(null) : undefined}
              className={cn(isCarousel ? cn("flex-shrink-0", cardWidth) : undefined, "relative")}
            >
              {isEditMode && (
                <button
                  type="button"
                  className="absolute inset-0 z-10 cursor-pointer"
                  onClick={() => onToggleSelect?.(entityId)}
                  aria-label={isSelected ? "Deselect" : "Select"}
                />
              )}
              {isEditMode && isSelected && (
                <div className="absolute inset-0 z-[9] rounded-xl border-3 border-white bg-white/20 dark:bg-transparent pointer-events-none" />
              )}
              {isEditMode && (
                <div className="absolute top-2 right-2 z-20 pointer-events-none">
                  <div
                    className={cn(
                      "h-6 w-6 rounded-md border-2 flex items-center justify-center shadow-sm",
                      isSelected ? "bg-white border-white" : "bg-white/80 border-white/60",
                    )}
                  >
                    {isSelected && <Check className="h-4 w-4 text-black" />}
                  </div>
                </div>
              )}
              <EntityHeader
                entityId={entityId}
                linkToEntity={!isEditMode}
                variant={isRows ? "row" : undefined}
                imageFit={imageFit}
                onHide={enableHide ? () => handleHide(entityId) : undefined}
                skipAuth={skipAuth}
              />
            </div>
          );
        })}
        {shouldVirtualize && carouselLimit < visibleIds.length && (
          <div ref={carouselSentinelRef} className="flex-shrink-0 w-1" />
        )}
      </div>

      {remainingRows > 0 && (
        <LoadMoreButton
          label={`Load more · ${remainingRows} remaining`}
          onClick={() => setRowLimit(limit => limit + ROWS_PAGE_SIZE)}
        />
      )}
    </div>
  );
}
